import {
  SHORT_LEAD_TIME_WITH_FLYING_BLUE_LEAD_TIME_DAYS, HIGH_RISK_LEAD_TIME_FOR_MAJORITY_CREDIT_IN_DAYS, HIGH_RISK_TRANSACTION_CREDIT_PERCENTAGE_THRESHOLD, MAX_STRIPE_CUSTOM_PAYTO_PAYMENT, PAYMENT_OPTIONS,
  PaymentMethod, PaypalSupplementaryData, RAZORPAY_PAYMENT_TYPE, SHORT_LEAD_TIME_WITH_GOOGLE_PAY_LEAD_TIME_DAYS, SHORT_LEAD_TIME_WITH_PAYPAL_LEAD_TIME_DAYS, STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE, PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED,
  STRIPE_TERMINAL_STORE_UTMS,
  STRIPE_TERMINAL_PAYMENT_TYPE,
} from 'constants/payment'
import { createSelector } from 'reselect'
import { isPrestige4nPromo } from 'checkout/constants/paymentPromotions'
import { getFlightItems, isStandaloneFlights } from 'checkout/selectors/view/flights'
import { cartIncludesTourV2 } from './toursv2'
import {
  isStripePaymentElementGiropayEnabled,
  isStripeCustomPaytoEnabled,
  isStripePaymentElementCardEnabled,
  isStripePaymentElementIDealEnabled,
  isStripePaymentElementKlarnaEnabled,
  isStripePaymentElementPayNowEnabled,
  isStripePaymentElementGrabPayEnabled,
  isStripePaymentElementWeChatPayEnabled,
} from 'lib/payment/stripeUtils'
import { isGooglePayEnabled } from 'lib/payment/googlePayUtils'
import { isStandaloneExperiences } from 'checkout/selectors/payment/experience'
import { getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { getCarHireItems } from 'checkout/selectors/view/carHire'
import { isLEStaffOrAdmin } from 'selectors/accountSelectors'
import { findPostPurchaseCheckout, isBedbankItem, isCruiseItem, isGiftCardItem, isLEAccommodationItem, isLEHotelItem, isTourV1Item, isTourV2Item, isNoProtectionInsuranceItem, isFlightItem, isMerchandiseItem } from 'lib/checkout/checkoutUtils'
import getPaymentMethods, { getPaymentMethodLimitsByRegion } from 'lib/payment/getPaymentMethods'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import getCheckoutTotal from 'checkout/selectors/payment/getCheckoutTotal'
import getPayableTotal from 'checkout/selectors/payment/getPayableTotal'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { getCartHasPropertyCollectItem } from './propertyCollectSelectors'
import {
  isBraintreeEnabled,
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
  getProjectedLeadTimeDaysByCartItems,
  isPayPalEnabled,
} from 'lib/payment/paymentUtils'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { isApplePayEnabled } from 'lib/payment/applePayUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import config from 'constants/config'
import { isSpoofed, isVelocityEnabled } from 'selectors/featuresSelectors'
import { getVelocityEligibleItems } from './partnerships'
import { VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS } from 'constants/partnerships'
import { mapObject } from 'lib/object/objectUtils'
import getCreditPayableAmount from 'checkout/selectors/payment/getCreditPayableAmount'
import moment from 'moment'
import shouldShowGiftCards from 'lib/payment/shouldShowGiftCards'
import { isLuxPlusSubscriptionInCart, isStandaloneLuxPlusSubscription } from '../view/luxPlusSubscription'
import { getVillaItems } from '../view/villa'
import { isSecurePaymentEnabled } from 'lib/payment/securePaymentUtils'
import { isCruiseCheckout } from '../view/cruise'
import { isPaymentTypeFlyingBlueEnabled } from 'lib/payment/flyingblueUtils'
import { isPaymentTypeAzupayPayIdEnabled } from 'lib/payment/azupayUtils'
import { isPostPurchaseAncillaryPayment } from './isPostPurchaseAncillaryPayment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import getExperienceItems from '../view/items/getExperienceItems'
import getTransferItems from '../view/items/getTransferItems'
import getMerchandiseItems from '../view/items/getMerchandiseItems'
import { getCheckoutLoyaltyBurnTotalInPoints } from '../loyalty/getCheckoutLoyaltyBurnTotals'
import getGiftCardPayableAmount from './getGiftCardPayableAmount'
import { isExpressCheckoutEnabled } from 'lib/payment/expressCheckoutUtils'
import { getCustomOfferItemType } from '../view/customOffer'
import { OFFER_TYPE_CUSTOM_OFFER_FLIGHT } from 'constants/offer'
import { isPaymentScheduleBalance } from './paymentSchedule/isPaymentScheduleBalance'

export const isDepositSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.DEPOSIT

export const isPaymentScheduleSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.PAYMENT_SCHEDULE

export const isDynamicPaymentScheduleSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.DYNAMIC_PAYMENT_SCHEDULE

const getCheckoutCurrency = (state: App.State) => state.checkout.cart.currencyCode

export const isRazorpayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => cartIncludesTourV2(state), // Temporarily disabled for tours, until it can be tested
  (currency, isStandaloneFlights, includesTourV2) => !includesTourV2 && getPaymentMethods(currency).includes(RAZORPAY_PAYMENT_TYPE) && !isStandaloneFlights,
)

export const isReserveForFreeSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.RESERVE_FOR_FREE

export const isStripePaymentElementCardAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isRazorpayAvailable(state),
  (regionCode, currencyCode, razorpayAvailable) => {
    return !!isStripePaymentElementCardEnabled(regionCode, currencyCode) && !razorpayAvailable
  },
)

const isGiftCardPurchase = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.some(isGiftCardItem),
)

export const isGiftCardEnabled = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (
    payableTotal,
    currencyCode,
    isGiftCard,
    isStandaloneFlight,
    hasMerchandiseItem,
    isLuxPlusSubscriptionInCart,
    isReserveForFreeSelected,
    isVelocityApplied,
  ): boolean => {
    // Disables gift card if there is no payment
    if (!payableTotal) return false

    return shouldShowGiftCards(currencyCode) &&
      !isGiftCard &&
      !isStandaloneFlight &&
      !hasMerchandiseItem &&
      !isLuxPlusSubscriptionInCart &&
      !isReserveForFreeSelected &&
      !isVelocityApplied
  },
)

const getGrandTotal = (state: App.State) => getCheckoutTotalsView(state).data.grandTotal

export const isFullPaymentSelected = (state: App.State) =>
  state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.FULL &&
  !isPostPurchaseAncillaryPayment(state)

const getClosestProjectedLeadTimeDays = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (cartItems) => getProjectedLeadTimeDaysByCartItems(cartItems),
)

export const isExpressPaymentAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => isApplePayAvailable(state),
  (state: App.State) => isGooglePayAvailable(state),
  (currency, isApplePayAvailable, isGooglePayAvailable): boolean => {
    return isExpressCheckoutEnabled(currency) && (isApplePayAvailable || isGooglePayAvailable)
  },
)
export const isGooglePayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => state.system.canPayWithGooglePay,
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (currency, canPayWithGooglePay, closestProjectedLeadTimeDays): boolean => {
    return isGooglePayEnabled(currency) &&
      canPayWithGooglePay &&
      // Adding card to Google pay does not always enforce OTP, high chance of fraud
      closestProjectedLeadTimeDays >= SHORT_LEAD_TIME_WITH_GOOGLE_PAY_LEAD_TIME_DAYS
  },
)

export const isApplePayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => state.system.canPayWithApplePay,
  (currency, canPayWithApplePay): boolean =>
    isApplePayEnabled(currency) && canPayWithApplePay)

export const isPromoCodeAllowed = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getVillaItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getCarHireItems(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.checkout.payment.partnerships,
  (state: App.State) => isLEStaffOrAdmin(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (state: App.State) => state.checkout.cart.mode === 'change-package',
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => getMerchandiseItems(state),
  (payableTotal,
    isStandaloneExperiences,
    accommodationItems,
    villaItems,
    flightItems,
    carHireItems,
    transferItem,
    partnerships,
    isLEStaff,
    isAncillaryPayment,
    isChangePackage,
    isStandaloneLuxPlusSubscription,
    experienceItems,
    isSpoofed,
    merchandiseItems,
  ): boolean => {
    const isExperience = experienceItems.length > 0

    if (!config.PROMO_CODES_ENABLED) {
      return false
    }

    // Disables promo code if there is no payment
    if (!payableTotal) return false

    if (partnerships?.velocity?.burn?.isApplied || isAncillaryPayment || isChangePackage) { return false }
    return isExperience || isStandaloneExperiences ||
      isStandaloneLuxPlusSubscription ||
      (accommodationItems.length > 0 && accommodationItems.every(item => isLEAccommodationItem(item) || isBedbankItem(item) || isTourV1Item(item) || isTourV2Item(item) || isCruiseItem(item))) ||
      (flightItems.length > 0 && flightItems.every(item => isFlightItem(item))) ||
      ((isLEStaff || isSpoofed) && carHireItems.length > 0) ||
      ((isLEStaff || isSpoofed) && merchandiseItems.length > 0) ||
      (villaItems.length > 0) ||
      (transferItem.length > 0)
  },
)

const cartHasBedbankItem = (state: App.State) => state.checkout.cart.items.some(item => item.itemType === 'bedbankHotel')

// TODO: implement- (copied this function over from checkoutPaymentSelectors, looks like it's been unimplemented for a long time)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isBookDatesOnly = (state: App.State) => false

/**
* @deprecated Will be removed once new PayPal integration is completed
*/
const isBraintreeAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => state.system.rawUserAgentString,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (currency, isGiftCard, isLuxPlusSubscriptionInCart, userAgentBrowserName, isPostPurchase) => {
    return isBraintreeEnabled(currency) &&
      !isGiftCard &&
      !isLuxPlusSubscriptionInCart &&
      !userAgentBrowserName?.toLowerCase().includes('mobileapp-android') && // Can be removed in early 2024 when paypal sdk upgraded on customer portal

      // We need to temporarily hide PayPal for change dates.
      // Paypal payments are breaking when when combined with credits for the creation of the new reservation.
      // Payments team have a ticket to fix this.  This code can be reverted when completed.
      // https://aussiecommerce.atlassian.net/browse/PP-2212
      // disable paypal flow for postpurchase due to payment issues, revisit when paypal commerce platform is used instead of braintree
      !isPostPurchase
  },
)

export const isPayPalAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutTotalsView(state).data.amountPayableByCredit,
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => getCheckoutLoyaltyBurnTotalInPoints(state),
  (currency, isGiftCard, isLuxPlusSubscriptionInCart, isPostPurchase, grandTotal, amountPayableByCredit, closestProjectedLeadTimeDays, useCredit, loyaltyBurnTotalInPoints) => {
    const creditPercentage = amountPayableByCredit / grandTotal
    const isHighRisk = useCredit && closestProjectedLeadTimeDays < HIGH_RISK_LEAD_TIME_FOR_MAJORITY_CREDIT_IN_DAYS && creditPercentage > HIGH_RISK_TRANSACTION_CREDIT_PERCENTAGE_THRESHOLD
    const isShortLeadTimePayPalOrder = closestProjectedLeadTimeDays <= SHORT_LEAD_TIME_WITH_PAYPAL_LEAD_TIME_DAYS
    return isPayPalEnabled(currency) && !isGiftCard && !isLuxPlusSubscriptionInCart && !isPostPurchase && !isHighRisk && !isShortLeadTimePayPalOrder && loyaltyBurnTotalInPoints === 0
  },
)

export const isPayLaterAvailable = createSelector(
  (state: App.State) => isPayPalAvailable(state),
  (state: App.State) => state.system.payPalSettings,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutTotalsView(state, 'paypal').data.payableNow,
  (isPayPalAvailable, payPalSettings, regionCode, paymentAmount) => {
    const regionSetting = payPalSettings?.payLaterSettings?.regionSettings[regionCode]
    if (!regionSetting || !isPayPalAvailable)
    {
      return false
    }

    return (regionSetting.minAmount <= paymentAmount && paymentAmount <= regionSetting.maxAmount && regionSetting.enabled)
  },
)

export const shouldShowPayLaterPaymentOption = createSelector(
  (state: App.State) => isPayLaterAvailable(state),
  (isPayLaterAvailable) => {
    return isPayLaterAvailable
  },
)

export const shouldShowPayLaterButton = createSelector(
  (state: App.State) => isPayLaterAvailable(state),
  (isPayLaterAvailable) => {
    // currently disabled
    return isPayLaterAvailable && false
  },
)

export const shouldShowDebitCardMerchantFees = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (cartItems, offers, postPurchaseMode) => {
    const hasFlashHotel = cartItems.some(item => {
      if (isLEHotelItem(item)) {
        const offer = offers[item.offerId]
        if (offer?.type === 'hotel') {
          return true
        }
      }
      return false
    })

    return hasFlashHotel && !postPurchaseMode
  },
)

export const isInternationalMerchantCardAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.checkout.stripePaymentMethod?.cardCountry,
  (state: App.State) => state.securePayment.stripePaymentMethod?.cardCountry,
  (currentRegionCode, checkoutCardCountry, securePaymentCardCountry) => {
    // Only enable international merchant fees when:
    // 1. Current region is AU
    // 2. Card country is not AU (i.e., international card)
    const cardCountry = checkoutCardCountry || securePaymentCardCountry
    return currentRegionCode === 'AU' && cardCountry && cardCountry !== 'AU'
  },
)

// For user with credits on the checkout page,
// the grand total could be > 35 while the app
// is fetching the user's credit balance (assumed
// to be 0). This causes Klarna Checkout to be
// mounted and immediately unmounted once the user
// data returns and causes the grand total to equal 0
const isKlarnaAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartHasBedbankItem(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => !hasBedbankItem &&
    isKlarnaEnabled(currency) &&
    isWithinKlarnaAvailabilityRange(cartTotal) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isKlarnaBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartHasBedbankItem(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => !hasBedbankItem &&
    isKlarnaBpEnabled(currency) &&
    isWithinKlarnaBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isAfterPayBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => isAfterPayBpEnabled(currency) &&
    !bookDatesOnly &&
    isWithinAfterPayBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isHoolahBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartIncludesTourV2(state), (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isHoolahBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinHoolahBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isAtomeBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartIncludesTourV2(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isAtomeBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinAtomeBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isWaaveBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => cartIncludesTourV2(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    includesTourV2,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isWaaveBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    // !isChangePackage && // Add this here once the new checkout has package change feature available
    isWithinWaaveBpAvailabilityRange(cartTotal, currency) &&
    !!config.WAAVE_BP_GLOBAL_CHECKOUT_ENABLED &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

export const isVelocityAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isVelocityEnabled(state),
  (state: App.State) => getVelocityEligibleItems(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => state.checkout.payment.useCredit,
  (currency, isVelocityEnabled, velocityEligibleItems, isStandaloneFlights, isStandaloneExperiences, isLuxPlusSubscriptionInCart, useCredit): boolean =>
    currency === 'AUD' &&
    isVelocityEnabled &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    velocityEligibleItems.length > 0 &&
    !isLuxPlusSubscriptionInCart &&
    !useCredit,
)

const isStripePaymentElementGiropayAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (regionCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementGiropayEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementIDealAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (regionCode, currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    currencyCode === 'EUR' &&
    !!isStripePaymentElementIDealEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementWeChatPayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, payableTotal, isLuxPlusSubscriptionInCart): boolean =>
    currencyCode === 'CNY' &&
    !!isStripePaymentElementWeChatPayEnabled(currencyCode) &&
    payableTotal <= 50000 &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementKlarnaAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => getPayableTotal(state),
  (currencyCode, regionCode, isLuxPlusSubscriptionInCart, isCruiseInCart, payableTotal): boolean =>
    !!isStripePaymentElementKlarnaEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart &&
    getPaymentMethodLimitsByRegion(regionCode)?.some(region => region.paymentMethod === STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE && payableTotal <= region.limit) &&
    !isCruiseInCart,
)

export const isStripePaymentElementPayNowAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementPayNowEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementGrabPayAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementGrabPayEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isPaymentTypeFlyingBlueAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (state: App.State) => isGiftCardPurchase(state),
  (regionCode, currencyCode, isLuxPlusSubscriptionInCart, closestProjectedLeadTimeDays, isGiftCard): boolean =>
    currencyCode === 'EUR' &&
    !!isPaymentTypeFlyingBlueEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart &&
    closestProjectedLeadTimeDays > SHORT_LEAD_TIME_WITH_FLYING_BLUE_LEAD_TIME_DAYS &&
    !isGiftCard,
)

export const isPaymentTypeAzupayPayIdAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => !!getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.payIdTargetedRelease),
  (state: App.State) => state.checkout.cart.items,
  (currencyCode, isPostPurchase, isLuxPlusSubscriptionInCart, isAzupayPayIdOptimizelyEnabled, items): boolean =>
  {
    const relevantItems = items.filter(item => !isNoProtectionInsuranceItem(item))
    return !!isPaymentTypeAzupayPayIdEnabled(currencyCode) &&
      !isLuxPlusSubscriptionInCart &&
      !isPostPurchase &&
      !isAzupayPayIdOptimizelyEnabled &&
      !(relevantItems.length > 1)
  },
)

export const isStripeTerminalEnabled = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => state.utm.medium,
  (state: App.State) => state.auth.account.isSpoofed,
  (_state: App.State) => config.STRIPE_TERMINAL_ENABLED_CHADSTONE,
  (_state: App.State) => config.STRIPE_TERMINAL_ENABLED_BONDI,
  (state: App.State) => getCustomOfferItemType(state),
  (currencyCode, utmSource, isSpoofed, isChadstoneFlagEnabled, isBondiFlagEnabled, customOfferType): boolean => {
    const isStripeTerminalPaymentEnabled = getPaymentMethods(currencyCode).includes(STRIPE_TERMINAL_PAYMENT_TYPE)
    const isCustomOfferFlight = customOfferType === OFFER_TYPE_CUSTOM_OFFER_FLIGHT

    const isFeatureEnabledForStore = (utmSource === 'chadstone_store' && isChadstoneFlagEnabled) ||
      (utmSource === 'bondi_store' && isBondiFlagEnabled)

    // The check for the utms is redundant at the moment, but we'll remove the isFeatureEnabledForStore in the future, and want to keep that check.
    return isFeatureEnabledForStore && isStripeTerminalPaymentEnabled && isSpoofed && STRIPE_TERMINAL_STORE_UTMS.includes(utmSource) && !isCustomOfferFlight
  },
)

export const cartIneligibleForStripeTerminal = createSelector(
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => isPaymentScheduleBalance(state),
  (cartHasPropertyCollectItem, isPaymentScheduleBalanceMode): boolean => {
    return cartHasPropertyCollectItem || isPaymentScheduleBalanceMode
  },
)

export const isStripeTerminalAvailable = createSelector(
  (state: App.State) => isStripeTerminalEnabled(state),
  (state: App.State) => cartIneligibleForStripeTerminal(state),
  (isEnabled, cartIneligibleForStripeTerminal): boolean => {
    return isEnabled && !cartIneligibleForStripeTerminal
  },
)

export const isStripeCustomPaytoAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getPayableAfterModifiers(state),
  (currencyCode, isLuxPlusSubscriptionInCart, payableAfterModifiers): boolean =>
    currencyCode === 'AUD' &&
    !!(isStripeCustomPaytoEnabled(currencyCode)) &&
    payableAfterModifiers <= MAX_STRIPE_CUSTOM_PAYTO_PAYMENT &&
    !isLuxPlusSubscriptionInCart,
)

const isGoCardlessAvailable = () => false // cleanup required

export const isPaymentRestrictedForCurrencyAndSpoofed = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.auth.account.isSpoofed,
  (currencyCode, isSpoofed): boolean =>
    isSpoofed && PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED.includes(currencyCode),
)

export const isSecurePaymentAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.items,
  (currencyCode, isSpoofed, postPurchase, items): boolean =>
    isSpoofed &&
    !postPurchase &&
    isSecurePaymentEnabled(currencyCode) &&
    !items.some(isTourV1Item),
)

export const getPaymentMethodAvailability = createSelector(
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => shouldShowPayLaterPaymentOption(state),
  (state: App.State) => state.checkout.promo,
  (state: App.State) => isBraintreeAvailable(state),
  (state: App.State) => isPayPalAvailable(state),
  (state: App.State) => isRazorpayAvailable(state),
  (state: App.State) => isKlarnaAvailable(state),
  (state: App.State) => isKlarnaBpAvailable(state),
  (state: App.State) => isAfterPayBpAvailable(state),
  (state: App.State) => isHoolahBpAvailable(state),
  (state: App.State) => isAtomeBpAvailable(state),
  (state: App.State) => isWaaveBpAvailable(state),
  (state: App.State) => isVelocityAvailable(state),
  (state: App.State) => isStripePaymentElementCardAvailable(state),
  (state: App.State) => isStripePaymentElementGiropayAvailable(state),
  (state: App.State) => isStripePaymentElementIDealAvailable(state),
  (state: App.State) => isStripePaymentElementKlarnaAvailable(state),
  (state: App.State) => isStripePaymentElementWeChatPayAvailable(state),
  (state: App.State) => isStripePaymentElementPayNowAvailable(state),
  (state: App.State) => isStripePaymentElementGrabPayAvailable(state),
  (state: App.State) => isStripeCustomPaytoAvailable(state),
  (_: App.State) => isGoCardlessAvailable(),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => isSecurePaymentAvailable(state),
  (state: App.State) => isPaymentTypeFlyingBlueAvailable(state),
  (state: App.State) => isPaymentTypeAzupayPayIdAvailable(state),
  (state: App.State) => isPaymentScheduleSelected(state),
  (state: App.State) => isDynamicPaymentScheduleSelected(state),
  (state: App.State) => isExpressPaymentAvailable(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => isPaymentRestrictedForCurrencyAndSpoofed(state),
  (state: App.State) => isStripeTerminalAvailable(state),
  (
    isFullPaymentSelected,
    shouldShowPayLaterPaymentOption,
    promotion,
    braintreeAvailable,
    paypalAvailable,
    razorpayAvailable,
    klarnaAvailable,
    klarnaBpAvailable,
    afterPayBpAvailable,
    hoolahBpAvailable,
    atomeBpAvailable,
    waaveBpAvailable,
    isVelocityAvailable,
    stripePaymentElementCardAvailable,
    stripePaymentElementGiropayAvailable,
    stripePaymentElementIDealAvailable,
    stripePaymentElementKlarnaAvailable,
    stripePaymentElementWeChatPayAvailable,
    stripePaymentElementPayNowAvailable,
    stripePaymentElementGrabPayAvailable,
    stripeCustomPaytoAvailable,
    goCardlessAvailable,
    isVelocityApplied,
    isReserveForFreeSelected,
    isSecurePaymentAvailable,
    flyingBlueAvailable,
    azupayPayIdAvailable,
    isPaymentScheduleSelected,
    isDynamicPaymentScheduleSelected,
    isExpressPaymentAvailable,
    cartHasPropertyCollectItem,
    isPaymentRestrictedForCurrencyAndSpoofed,
    stripeTerminalAvailable,
  ): Record<PaymentMethod, boolean> => {
    const paymentMethodAvailabilities: Record<string, boolean> = {

      stripe_payment_element_card: stripePaymentElementCardAvailable && !isPaymentRestrictedForCurrencyAndSpoofed,

      stripe_payment_element_giropay: stripePaymentElementGiropayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_ideal: stripePaymentElementIDealAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_klarna: stripePaymentElementKlarnaAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_wechatpay: stripePaymentElementWeChatPayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_paynow: stripePaymentElementPayNowAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_grabpay: stripePaymentElementGrabPayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,

      braintree: braintreeAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      paypal: paypalAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      paylater: shouldShowPayLaterPaymentOption && isFullPaymentSelected && !cartHasPropertyCollectItem,
      razorpay: razorpayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      klarna: klarnaAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      klarna_bp: klarnaBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      afterpay_bp: afterPayBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      hoolah_bp: hoolahBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      velocity: isVelocityAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      atome_bp: atomeBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      waave_bp: waaveBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_custom_payto: stripeCustomPaytoAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      gocardless: goCardlessAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      secure_payment: isSecurePaymentAvailable && !isReserveForFreeSelected && !isPaymentScheduleSelected && !isDynamicPaymentScheduleSelected && !cartHasPropertyCollectItem,
      flying_blue: flyingBlueAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      azupay_pay_id: azupayPayIdAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      express: isExpressPaymentAvailable && !cartHasPropertyCollectItem,
      stripe_terminal: stripeTerminalAvailable,
    }

    if (isVelocityApplied) {
      for (const key of Object.keys(paymentMethodAvailabilities)) {
        if (VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(key)) {
          continue
        }
        paymentMethodAvailabilities[key] = false
      }
    }

    const applyPromotionModifiers = promotion && promotion.allowedPaymentMethods.length > 0
    const applyPartnershipModifiers = isVelocityApplied

    // @ts-ignore
    return mapObject(paymentMethodAvailabilities, (value, payMethod: string) => {
      let result = value

      if (applyPromotionModifiers) {
        result = result && promotion.allowedPaymentMethods.includes(payMethod)
      }

      if (applyPartnershipModifiers) {
        result = result && VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(payMethod)
      }

      return result
    })
  },
)

export const getCartHasMerchandiseItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isMerchandiseItem),
)

export const isCreditPayable = createSelector(
  (state: App.State) => getCreditPayableAmount(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isLEStaffOrAdmin(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.promo?.code,
  (
    creditAmount,
    isStandaloneFlights,
    isGiftCardPurchase,
    hasPropertyCollectItem,
    isVelocityBurnApplied,
    hasMerchandiseItem,
    isStaffOrAdmin,
    userIsSpoofed,
    promoCode,
  ): boolean => {
    const alwaysBlockList = [
      isStandaloneFlights,
      isGiftCardPurchase,
      hasPropertyCollectItem,
    ]

    // list restricted to internal staff or spoofed users
    const restrictedList = [
      hasMerchandiseItem,
    ]

    if (isPrestige4nPromo(promoCode)) {
      return false
    }

    if (creditAmount <= 0 || isVelocityBurnApplied) {
      return false
    }

    if (alwaysBlockList.some(blocked => blocked)) {
      return false
    }

    if (restrictedList.some(restricted => restricted)) {
      return isStaffOrAdmin || userIsSpoofed
    }

    return true
  },
)

export const isGiftCardPayable = createSelector(
  (state: App.State) => getGiftCardPayableAmount(state),
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (giftCardPayableAmount, cards, isGiftCard, hasMerchandiseItem, isReserveForFreeSelected, isVelocityApplied, cartHasPropertyCollectItem): boolean => {
    return giftCardPayableAmount > 0 && cards.length > 0 && !isGiftCard && !hasMerchandiseItem && !isReserveForFreeSelected && !isVelocityApplied && !cartHasPropertyCollectItem
  },
)

const toISO601Format = (date: string) => moment.utc(date).format('YYYY-MM-DDT hh:mm:ss.SSSZ')
export const getPaypalSupplementaryData = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.tourV2Offers,
  (cartItems, offers, bedbankOffers, tourV2Offers): PaypalSupplementaryData => {
    const leHotelItem = cartItems.find(isLEHotelItem)
    const bedbankItem = cartItems.find(isBedbankItem)
    const tourV1Item = cartItems.find(isTourV1Item)
    const tourV2Item = cartItems.find(isTourV2Item)

    if (tourV1Item) {
      const pkg = offers[tourV1Item.offerId]?.packages.find(p => p.id === tourV1Item.packageId)
      return {
        ota_service_start_date: toISO601Format(tourV1Item.startDate ?? ''),
        ota_service_end_date: toISO601Format(tourV1Item.endDate ?? ''),
        ota_start_country: pkg?.tour?.geoData.countryCode ?? undefined,
      }
    }
    else if (tourV2Item) {
      const offer = tourV2Offers[tourV2Item.offerId]
      const variation = offer?.variations[tourV2Item.purchasableOption.fkVariationId]
      return {
        ota_service_start_date: toISO601Format(tourV2Item.startDate),
        ota_service_end_date: toISO601Format(tourV2Item.endDate),
        ota_start_country: variation?.countriesVisited?.[0],
      }
    }
    else if (leHotelItem) {
      const offer = offers[leHotelItem.offerId]
      const supplementaryData = {
        ota_start_country: offer?.property?.geoData.countryCode,
      } as PaypalSupplementaryData
      if (leHotelItem.reservationType === 'instant_booking') {
        supplementaryData.ota_service_start_date = toISO601Format(leHotelItem.checkIn)
        supplementaryData.ota_service_end_date = toISO601Format(leHotelItem.checkOut)
      }
      return supplementaryData
    }
    else if (bedbankItem) {
      const offer = bedbankOffers[bedbankItem.offerId]
      return {
        ota_service_start_date: toISO601Format(bedbankItem.checkIn),
        ota_service_end_date: toISO601Format(bedbankItem.checkOut),
        ota_start_country: offer?.property.address.countryCode,
      }
    }
    return {}
  },
)

export const isAnyInstalmentPayAvailable = createSelector(
  (state: App.State) => isKlarnaAvailable(state),
  (state: App.State) => isAfterPayBpAvailable(state),
  (state: App.State) => isKlarnaBpAvailable(state),
  (state: App.State) => isHoolahBpAvailable(state),
  (state: App.State) => isAtomeBpAvailable(state),
  (klarna, afterPayBp, klarna_bp, hoolah_bp, atome_bp) => klarna || afterPayBp || klarna_bp || hoolah_bp || atome_bp,
)
